import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '@/lib/utils';
import { Eye, Zap, RefreshCw, Code2 } from 'lucide-react';

const features = [
    {
        title: "Real-Time Development",
        description: "Experience instant feedback as you code. Every change you make in Preswald is immediately reflected in the preview, eliminating the need for manual refreshes or rebuilds.",
        icon: RefreshCw,
    },
    {
        title: "Full-Stack Preview",
        description: "Preview your entire application stack in one place. From frontend components to backend logic, see how everything works together in real-time.",
        icon: Code2,
    },
    {
        title: "Interactive Development",
        description: "Build and test interactive features on the fly. Debug user interactions, state management, and component behavior in real-time.",
        icon: Zap,
    },
    {
        title: "Multi-Device Testing",
        description: "Test your application across different screen sizes and devices simultaneously. Ensure your app looks and works great everywhere.",
        icon: Eye,
    }
];

const LivePreviewsPage: React.FC = () => {
    // Sample data for the mockup
    const revenueData = [
        { name: 'Jan', value: 4000 },
        { name: 'Feb', value: 3000 },
        { name: 'Mar', value: 5000 },
        { name: 'Apr', value: 2780 },
        { name: 'May', value: 1890 },
        { name: 'Jun', value: 2390 },
    ];

    const categoryData = [
        { name: 'Electronics', value: 4000 },
        { name: 'Furniture', value: 3000 },
        { name: 'Clothing', value: 2000 },
        { name: 'Accessories', value: 2780 },
    ];

    return (
        <div className="min-h-screen bg-background">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            <main className="container mx-auto px-4 py-6 sm:py-12 flex-grow mt-16">
                <div className="mx-auto max-w-[980px]">
                    {/* Hero Section */}
                    <div className="flex flex-col items-center text-center space-y-4 mb-12">
                        <div className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                            Live Preview
                        </div>
                        <h1 className={cn(
                            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            See Your Changes Instantly
                        </h1>
                        <p className="mx-auto max-w-[700px] text-lg text-muted-foreground sm:text-xl">
                            Build and see your web applications come to life in real-time. No more manual refreshes or rebuilds needed.
                        </p>
                    </div>

                    {/* Demo Section */}
                    <div className="mb-16">
                        <div className="overflow-hidden rounded-lg border bg-background shadow-xl">
                            <div className="flex h-14 items-center justify-between border-b px-4">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <span className="text-sm font-medium">Live Preview</span>
                                </div>
                            </div>
                            <div className="h-[600px] overflow-hidden">
                                <div className="p-6 overflow-y-auto h-full">
                                    <div className="space-y-6">
                                        {/* Header */}
                                        <div>
                                            <h2 className="text-xl font-semibold text-gray-900">Sales Dashboard</h2>
                                            <p className="text-sm text-gray-500">Real-time sales analytics and insights</p>
                                        </div>

                                        {/* Charts Grid */}
                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Revenue Chart */}
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h3 className="text-sm font-medium text-gray-900 mb-2">Monthly Revenue</h3>
                                                <div className="h-[200px]">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart data={revenueData}>
                                                            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                                                            <XAxis dataKey="name" className="text-xs" />
                                                            <YAxis className="text-xs" />
                                                            <Tooltip />
                                                            <Area 
                                                                type="monotone" 
                                                                dataKey="value" 
                                                                stroke="#2563eb" 
                                                                fill="#3b82f6" 
                                                                fillOpacity={0.2}
                                                            />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            {/* Category Chart */}
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h3 className="text-sm font-medium text-gray-900 mb-2">Revenue by Category</h3>
                                                <div className="h-[200px]">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={categoryData}>
                                                            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                                                            <XAxis dataKey="name" className="text-xs" />
                                                            <YAxis className="text-xs" />
                                                            <Tooltip />
                                                            <Bar dataKey="value" fill="#3b82f6" />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats Grid */}
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <p className="text-sm text-gray-500">Total Revenue</p>
                                                <p className="text-2xl font-semibold text-gray-900">$24,500</p>
                                                <p className="text-xs text-green-600">+12.5% from last month</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <p className="text-sm text-gray-500">Active Users</p>
                                                <p className="text-2xl font-semibold text-gray-900">1,234</p>
                                                <p className="text-xs text-green-600">+8.2% from last month</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <p className="text-sm text-gray-500">Conversion Rate</p>
                                                <p className="text-2xl font-semibold text-gray-900">3.2%</p>
                                                <p className="text-xs text-red-600">-0.5% from last month</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 mb-16">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <feature.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default LivePreviewsPage; 